import { useCallback, useMemo, useState } from "react";
import { debounce } from "../utils";

const useModelEventsManager = () => {
	const [hasListeners, setHasListeners] = useState(false);

	const listeners = useMemo(() => ({
		pointerOver: new Set(),
		pointerOut: new Set(),
		click: new Set(),
	}), []);

	const updateHasListeners = () => {
		const foundListeners = !!Object.values(listeners)
			.find((set) => set.size > 0);

		if (foundListeners !== hasListeners) {
			setHasListeners(foundListeners);
		}
	};

	const addEventListener = (name, listener) => {
		listeners[name].add(listener);
		updateHasListeners();
		//return a function to remove the listener
		return () => {
			listeners[name].delete(listener);
			updateHasListeners();
		};
	};

	const getModelEventHandler = (name) => debounce((e) => {
		listeners[name].forEach((listener) => listener(e));
	}, 200);

	/**
	 * adding pointer over/out events kills the performance so we only re-render with the callbacks when there's actually
	 * registered handlers. Even no-op handlers will make r3f add ray-casting to track the pointer and degrade perf!
	 */
	const onPointerOver = useCallback(listeners["pointerOver"]?.size ? getModelEventHandler("pointerOver") : null, [listeners["pointerOver"]?.size]);
	const onPointerOut = useCallback(listeners["pointerOut"]?.size ? getModelEventHandler("pointerOut") : null, [listeners["pointerOut"]?.size]);
	const onClick = useCallback(listeners["click"]?.size ? getModelEventHandler("click") : null, [listeners["click"]?.size]);

	return {
		addPointerOverListener: (listener) => addEventListener("pointerOver", listener),
		addPointerOutListener: (listener) => addEventListener("pointerOut", listener),
		addClickListener: (listener) => addEventListener("click", listener),
		onPointerOver,
		onPointerOut,
		onClick,
	};
};

export default useModelEventsManager;
